/* External */
import { memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';

/* Components */
import FocusableButton from '../FocusableButton';

/* Providers */
import { useGtmLocalContext } from '../../providers/GtmLocalProvider';

/* Hooks */
import useAddRemoveWatchList from '../../hooks/useAddRemoveWatchList';

/* Utils */
import { isMyListEnabled } from '../../utils/constants';
import { Content, Show } from '../../storage/models';
import {
  formatContentData,
  GTM_CATEGORIES,
  GTM_EVENT_NAMES,
  GTM_SUBCATEGORIES,
} from '../../utils/googleTags';

/* Style */
import './AddRemoveWatchListButton.scss';

function AddRemoveWatchListButton({
  className,
  contentId,
  vmsId,
  type,
  vrioAssetId,
  focusRestorable,
  onBecameFocused,
  hasFocusedChild,
  content,
  locationState,
}) {
  const { commonTaggingEvent } = useGtmLocalContext();
  const { isAddedWatchList, handleAddRemoveWatchList } = useAddRemoveWatchList({
    vmsId, contentId, type, vrioAssetId,
  });

  const handleAddRemoveTaggingEvent = useCallback(() => {
    handleAddRemoveWatchList();
    commonTaggingEvent({
      category: GTM_CATEGORIES.MY_LIST_DATA,
      subCategory: GTM_SUBCATEGORIES.MY_LIST.ADD_REMOVE,
      eventName: isAddedWatchList ? GTM_EVENT_NAMES.MY_LIST.REMOVE : GTM_EVENT_NAMES.MY_LIST.ADD,
      data: {
        content: {
          ...formatContentData({
            content,
            rest: { isLive: false },
          }),
        },
        isKids: false,
        tab: 'pdp',
        providerName: locationState?.providerName,
      },
    });
  }, [
    commonTaggingEvent,
    content,
    handleAddRemoveWatchList,
    isAddedWatchList,
    locationState?.providerName,
  ]);

  if (!isMyListEnabled) {
    // eslint-disable-next-line unicorn/no-null -- React doesn't support returning undefined
    return null;
  }

  return (
    <FocusableButton
      className={`${hasFocusedChild ? 'dtv-html5-c-add-remove-watch-list-button--focused' : ''} dtv-html5-c-add-remove-watch-list-button ${className}`}
      primary
      focusRestorable={focusRestorable}
      onEnterPress={handleAddRemoveTaggingEvent}
      onBecameFocused={onBecameFocused}
    >
      <div
        className={`dtv-html5-c-add-remove-watch-list-button__icon ${isAddedWatchList ? 'dtv-html5-c-add-remove-watch-list-button__icon--added' : ''}`}
      >
        <span className="dtv-html5-c-add-remove-watch-list-button__line-one">
          <span className="dtv-html5-c-add-remove-watch-list-button__line" />
        </span>
        <span className="dtv-html5-c-add-remove-watch-list-button__line-two">
          <span className="dtv-html5-c-add-remove-watch-list-button__line" />
        </span>
      </div>
    </FocusableButton>
  );
}

AddRemoveWatchListButton.propTypes = {
  className: PropTypes.string,
  focusRestorable: PropTypes.bool,
  contentId: PropTypes.string,
  vmsId: PropTypes.string,
  onBecameFocused: PropTypes.func,
  hasFocusedChild: PropTypes.bool.isRequired,
  type: PropTypes.string,
  vrioAssetId: PropTypes.string,
  content: PropTypes.oneOfType([
    PropTypes.instanceOf(Content),
    PropTypes.instanceOf(Show),
  ]),
  locationState: PropTypes.instanceOf(Object),
};

AddRemoveWatchListButton.defaultProps = {
  className: '',
  focusRestorable: undefined,
  contentId: '',
  vmsId: '',
  onBecameFocused: undefined,
  type: undefined,
  vrioAssetId: '',
  content: undefined,
  locationState: undefined,
};

export default memo(withFocusable(
  { trackChildren: true },
)(memo(AddRemoveWatchListButton)));
